import $ from 'jquery';

import registerJQueryPlugin from 'styleguide/lib/register-jquery-plugin';
import mergeOptions from 'styleguide/lib/merge-options';

export const PLUGIN_NAME = 'shopTheLookModal';
export const DEFAULTS = {
};

import addToBag from'../entry-points/pdp/add-to-bag';

/**
 * Displays a confirmation block within the modal content.
 *
 * @param {jQuery} $modalContent - The jQuery object representing the modal content.
 */
const showConfirmation = ($modalContent) => {
  const $confirmationBlock = $modalContent.find('.js-stl-confirmation');

  // Fade in the confirmation block
  $confirmationBlock.fadeIn(500);

  // Fade out the confirmation block after 3 seconds
  setTimeout(() => {
    $confirmationBlock.fadeOut(500);
  }, 3000);
};

/**
 * Handles the form submission event for adding a product size to the bag.
 *
 * @param {jQuery} $slickCarousel - The slick carousel jQuery object.
 * @param {jQuery} $modalContent - The jQuery object representing the modal content.
 */
const handleFormSubmit = ($slickCarousel, $modalContent) => {
  const $currentSlide = $slickCarousel.find('.slick-current.slick-active');
  const productCode = $currentSlide.find('.js-product-slide').data('productCode');
  const $formToSubmit = $(`#sizes-${productCode}`);
  const $sizeOption = $modalContent.find('.js-stl-size-options:visible');

  // Attach submit event handler to the form
  $formToSubmit.on('submit', (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    const $size = $sizeOption.length ? $sizeOption.find(":selected").val() : 'all';
    addToBag(productCode, $size, false, false);
    showConfirmation($modalContent);
  });
};

/**
 * Updates the "Add to Bag" button text based on the product's preorder status.
 *
 * @param {boolean} preorder - Indicates if the product is a preorder.
 * @param {jQuery} $modalContent - The jQuery object representing the modal content.
 */
const updateAddToBagButton = (preorder, $modalContent) => {
  const $addToBagBtn = $modalContent.find('.js-stl-add-to-bag:visible');
  const addToBagText = $addToBagBtn.data('addToBagText');
  const preorderText = $addToBagBtn.data('preorderText');

  // Update button text based on preorder status
  $addToBagBtn.text(preorder ? preorderText : addToBagText);
};

/**
 * Applies the appropriate UI updates based on the selected product size.
 *
 * @param {Object} selectedSizeData - Data attributes for the selected size.
 * @param {jQuery} $modalContent - The jQuery object representing the modal content.
 */
const setUseCases = (selectedSizeData, $modalContent) => {
  const { preorder } = selectedSizeData;
  updateAddToBagButton(preorder, $modalContent);
};

/**
 * Displays the relevant product details based on the current slide.
 *
 * @param {jQuery} $slickCarousel - The slick carousel jQuery object.
 * @param {string} sizesContainer - Selector for the size options container.
 * @param {jQuery} $modalContent - The jQuery object representing the modal content.
 */
const showProductDetails = ($slickCarousel, sizesContainer, $modalContent) => {
  const $currentSlide = $slickCarousel.find('.slick-current.slick-active');
  const productCode = $currentSlide.find('.js-product-slide').data('productCode');
  const productDetails = $(`.js-details-block-${productCode}`);
  const $sizesSelect = $modalContent.find('.js-stl-size-options:visible');

  // Reset size selection and display relevant product details
  $sizesSelect.off('change');
  $(`${sizesContainer} > div`).hide();
  productDetails.show();
};

/**
 * Sets up the size change handler to update the UI based on the selected size.
 *
 * @param {jQuery} $modalContent - The jQuery object representing the modal content.
 * @param {jQuery} $slickCarousel - The slick carousel jQuery object.
 */
const setSizeUpdate = ($modalContent, $slickCarousel) => {
  const $sizesSelect = $modalContent.find('.js-stl-size-options:visible');
  const $currentSlide = $slickCarousel.find('.slick-current.slick-active');
  const isOneSize = $currentSlide.find('.js-product-slide').data('oneSize');

  if (!isOneSize) {
    // Attach change event handler to the size selector
    $sizesSelect.on('change', function () {
      const selectedSize = $(this).find('option:selected');
      const selectedSizeData = selectedSize.data();
      setUseCases(selectedSizeData, $modalContent);
    });
  }
};

/**
 * Handles updates to the carousel and triggers relevant UI updates.
 *
 * @param {jQuery} $slickCarousel - The slick carousel jQuery object.
 * @param {string} sizesContainer - Selector for the size options container.
 * @param {jQuery} $modalContent - The jQuery object representing the modal content.
 */
const handleSwipeUpdate = ($slickCarousel, sizesContainer, $modalContent) => {
  $slickCarousel.on('afterChange', () => {
    showProductDetails($slickCarousel, sizesContainer, $modalContent);
    setSizeUpdate($modalContent, $slickCarousel);
    handleFormSubmit($slickCarousel, $modalContent);
  });
};

/**
 * Initializes and opens the modal, setting up carousel and event handlers.
 *
 * @param {jQuery} triggerElement - The element that triggered the modal opening.
 */
const openModal = (triggerElement) => {
  const modalHref = $(triggerElement).data('href');
  $.modal({
    url: modalHref,
    triggerOpen: true,
    type: 'toast',
    triggerElement,
    addModifierClass: 'modal--padding-rl-0',
    onComplete: (opts) => {
      const $modalContent = opts.content;
      const sizesContainer = '.js-product-sizes';
      const $slickCarousel = $('.js-slick-stl');

      // Initialize the slick carousel
      $slickCarousel.imageCarousel({
        slidesToShow: 1,
        slidesToScroll: 1,
        centerMode: true,
        dots: false,
        slickContainer: '.js-slick-container',
        counterContainer: '.js-slick-counter',
        accessibility: true,
      });

      handleSwipeUpdate($slickCarousel, sizesContainer, $modalContent);
      setSizeUpdate($modalContent, $slickCarousel);
      handleFormSubmit($slickCarousel, $modalContent);
    },
  });
};

/**
 * Sets up the click event listener to open the modal.
 *
 * @param {jQuery} $el - The jQuery object for the modal trigger element.
 */
const setEventListeners = ($el) => {
  $el.on('click', (e) => {
    const triggerElement = $(e.currentTarget);
    openModal(triggerElement);
  });
};

/**
 * Initializes the "Shop the Look" modal and sets up event listeners.
 */
export default function shopTheLookModal(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions(DEFAULTS, options, $el);
  setEventListeners($el);
}

// Expose the function as a jQuery plugin for ease of use
registerJQueryPlugin(PLUGIN_NAME, shopTheLookModal);



// WEBPACK FOOTER //
// ./src/scripts/modules/shop-the-look.js